import { useEffect, useState } from 'react';
import { attachmentKindFor } from '@supporchat/shared';
import { formatBytes } from '../lib/format';

/**
 * O arquivo em espera, acima do compositor.
 *
 * Existe para responder uma pergunta antes do envio: é este mesmo? Foto de peça e
 * captura de tela têm nome de máquina (`IMG_20240812_143355.jpg`), então o nome
 * não responde — a miniatura responde. Para documento não há o que mostrar, e aí
 * nome e tamanho são a conferência possível.
 *
 * Fica em faixa própria, e não dentro do compositor, pelo mesmo motivo da faixa
 * de assinatura: lá ela disputaria espaço com a caixa de texto e os botões.
 */
export function AttachmentPreview({
  file,
  sending,
  onRemove,
}: {
  file: File;
  sending: boolean;
  onRemove: () => void;
}) {
  const kind = attachmentKindFor(file.type) ?? 'document';
  const thumbnail = useObjectUrl(kind === 'image' ? file : null);

  return (
    <div className="attachment-pending">
      {thumbnail ? (
        <img className="attachment-pending__thumb" src={thumbnail} alt="" />
      ) : (
        <span className="attachment-pending__icon" aria-hidden="true">
          {kind === 'video' ? '🎬' : '📎'}
        </span>
      )}

      <span className="attachment-pending__info">
        <strong className="attachment-pending__name">{file.name}</strong>
        <span className="muted">
          {LABELS[kind]} · {formatBytes(file.size)}
          {sending ? ' · enviando...' : ' · escreva uma legenda ou envie assim'}
        </span>
      </span>

      <button className="btn btn--sm" disabled={sending} onClick={onRemove}>
        Remover
      </button>
    </div>
  );
}

const LABELS: Record<'image' | 'video' | 'document', string> = {
  image: 'Foto',
  video: 'Vídeo',
  document: 'Documento',
};

/**
 * URL local do arquivo, revogada quando ele sai de espera.
 *
 * Sem o revoke, cada foto trocada no compositor deixaria os bytes dela retidos
 * até a aba fechar — e quem atende passa o dia na mesma aba.
 */
function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }

    const created = URL.createObjectURL(file);
    setUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [file]);

  return url;
}
